<script setup lang="ts">
    interface StampedMainWidgetProps {
        productId?: string | null | undefined;
        shopTitle: string | null | undefined;
        imageUrl: string | undefined;
        description: string | null | undefined;
        productSku: string | undefined;
        productType: string | undefined;
    }
    defineProps<StampedMainWidgetProps>();

    const { t, isoLocale } = useLocales();
    const route = useRoute();

    const stampedContainer = ref<HTMLElement | null>();
    const hasReviewContent = ref(false);

    const checkReviewContent = async (retriesLeft = 50): Promise<void> => {
        if (!retriesLeft) return;
        await wait(100);
        if (stampedContainer.value?.querySelector('.stamped-container') === null) {
            return await checkReviewContent(--retriesLeft);
        }
    };

    onMounted(async () => {
        // Init and insert Stamped.io disclaimer
        await stampedInit();
        await wait(1000);
        throttledReloadUGC();
        const inputElement = document.getElementById('stamped-button-submit');
        const disclaimerText = t('stampedIo.disclaimer');
        const disclaimerLinkText = t('stampedIo.disclaimer_link');
        const disclaimerLinkHref = t('stampedIo.disclaimer_href');
        const stampedDisclaimer = `<p class="basis-full mt-5 border-t border-woom-grey-mid pt-5 font-mono text-xs text-woom-grey-moon">${disclaimerText} <a class="underline" href="${disclaimerLinkHref}" target="_blank">${disclaimerLinkText}</a></p>`;
        inputElement?.insertAdjacentHTML('afterend', stampedDisclaimer);

        // nuxt might first calculate scroll offset incorrectly as content shift loading above the widget, then we scrollIntoView to get correct place, in theory
        if (route.hash === '#reviews') stampedContainer.value?.scrollIntoView?.({ behavior: 'smooth' });
        await checkReviewContent();
        hasReviewContent.value = true;
    });
</script>

<template>
    <section
        ref="stampedContainer"
        id="reviews"
        class="mol-stamped-widget relative min-h-[50vh] py-8 md:py-10">
        <lazy-atm-loader-bike
            v-show="!hasReviewContent"
            class="absolute h-full w-full" />
        <atm-grid>
            <div class="col-span-2 md:col-span-12">
                <atm-heading
                    level="h2"
                    size="xl">
                    {{ $t('stampedIo.heading') }}
                </atm-heading>
            </div>
            <client-only>
                <transition
                    enter-from-class="opacity-0"
                    enter-active-class="duration-300 ease-in"
                    enter-to-class="opacity-100">
                    <div
                        v-show="hasReviewContent"
                        :data-product-id="productId"
                        :data-name="shopTitle"
                        :data-url="route.path"
                        :data-image-url="imageUrl"
                        :data-description="removeTags(description)"
                        :data-product-sku="productSku"
                        :data-widget-language="isoLocale"
                        :data-product-type="productType"
                        data-offset="112"
                        data-offset-mobile="112"
                        id="stamped-main-widget"
                        class="col-span-2 overflow-hidden md:col-span-12"></div>
                </transition>
            </client-only>
        </atm-grid>
    </section>
</template>

<style scoped>
    :deep(#stamped-main-widget) {
        @apply m-0;
    }
    /* global black star color are set in dashboard settings. main widget should have red stars */
    :deep(#stamped-main-widget .stamped-fa-star),
    :deep(#stamped-main-widget .stamped-fa-star-half-o) {
        @apply !text-woom-red;
    }
    /* star graph */
    :deep(.summary-rating-title:before) {
        @apply !text-woom-red;
    }
    /* setting global font */
    :deep(.stamped-container) {
        @apply font-[WoomMain];
    }
    /* setting secondary font */
    :deep(.review-location),
    :deep(.created),
    :deep(.stamped-review-vote) {
        @apply font-mono;
    }
    /* main heading */
    :deep(.stamped-summary-text-1) {
        @apply text-4xl md:text-5xl;
    }
    /* main description */
    :deep(.stamped-summary-text) {
        @apply font-mono text-xs;
    }
    /* Hide bike size range display and sharing buttons on successful form submission */
    :deep(.stamped-review-options),
    :deep(.stamped-share-links),
    :deep(.stamped-thank-you p:nth-of-type(2)) {
        @apply !hidden;
    }
    /* tabs */
    :deep(div.stamped-container ul.stamped-tabs li) {
        @apply !text-base;
    }
    /* filters */
    :deep(.stamped-filters-wrapper) {
        @apply !w-[calc(100%-1px)];
    }
    :deep(.stamped-reviews-filter-label) {
        @apply !text-base;
    }
    :deep(ul.stamped-summary-keywords-list) {
        @apply max-h-max min-h-fit overflow-visible;
    }
    :deep(ul.stamped-summary-keywords-list li) {
        @apply inline-flex h-10 items-center rounded-md border-2 border-solid border-woom-grey-mid bg-white py-0 text-base font-bold text-woom-black;
    }
    :deep(ul.stamped-summary-keywords-list li:not(.selected):hover) {
        @apply border-woom-black transition-colors duration-300;
    }
    :deep(ul.stamped-summary-keywords-list li.selected) {
        @apply !bg-woom-grey-mid;
    }
    /* form */
    :deep(.new-question-form) {
        @apply !p-4;
    }
    /* form checkbox */
    :deep(#stamped-main-widget input[type='checkbox']),
    :deep(#stamped-main-widget input[type='radio']) {
        /* tailwind class don't work here */
        accent-color: #eb0613;
    }
    /* form select */
    :deep(#stamped-filter-select),
    :deep(#stamped-sort-select),
    :deep(#stamped-sort-select2),
    :deep(.stamped-filter-select) {
        @apply h-14 !overflow-hidden text-ellipsis whitespace-nowrap rounded !border-woom-black !bg-[url('../../assets/images/stamped-arrow-down.png')] p-4 pr-8 text-base text-woom-black;
    }
    :deep(#stamped-sort-select) {
        @apply mt-3;
    }
    /* form radio */
    :deep(.stamped-form-label-recommend-yes),
    :deep(.stamped-form-label-recommend-no) {
        @apply inline-flex items-center gap-1;
    }
    /* form action buttons wrapper */
    :deep(.stamped-form-actions) {
        @apply mt-6 flex flex-wrap text-left;
    }
    /* form labels */
    :deep(.stamped-form-label),
    :deep(.stamped-form-custom-question label) {
        @apply text-base text-woom-black text-opacity-60;
    }
    /* form inputs */
    :deep(.stamped-form-input),
    :deep(.stamped-form-custom-question input) {
        @apply rounded !border-woom-black text-base text-woom-black;
    }
    :deep(.stamped-form-input-text),
    :deep(.stamped-form-input-email) {
        @apply px-4 py-3;
    }
    /* buttons solid */
    :deep(.stamped-summary-actions-newreview),
    :deep(.stamped-button-primary) {
        @apply flex !h-12 items-center justify-center rounded-md border-none bg-woom-black px-4 font-[WoomMain] text-base font-bold text-white !shadow-none;
    }
    :deep(.stamped-summary-actions-newreview:before) {
        @apply text-white;
    }
    /* buttons outline */
    :deep(.stamped-summary-actions-newquestion),
    :deep(.stamped-summary-actions-clear) {
        @apply box-border inline-flex h-12 items-center justify-center rounded-md border-2 border-solid border-woom-black px-4 font-[WoomMain] text-base font-bold text-woom-black !shadow-none;
    }
    :deep(.stamped-summary-actions-clear) {
        @apply h-10 text-xs;
    }
    :deep(.stamped-summary-actions-mobile-filter) {
        @apply box-border rounded-md border-2 border-solid border-woom-black;
    }
    :deep(.stamped-summary-actions-newquestion:before),
    :deep(.stamped-summary-actions-mobile-filter i:before),
    :deep(.stamped-summary-actions-clear:before) {
        @apply pr-2 text-woom-black;
    }
    /* file upload button */
    :deep(.stamped-file-uploader label[for='stamped-file-uploader-input'] > span) {
        @apply box-border flex h-12 items-center justify-center !rounded-md !border-2 border-woom-black !py-0 px-4 !font-[WoomMain] !text-base font-bold text-woom-black;
    }
    /* file upload container */
    :deep(.stamped-file-holder) {
        @apply order-5 flex w-full gap-3;
    }
    /* review */
    :deep(.author),
    :deep(.stamped-review-content-body) {
        @apply text-base;
    }
    :deep(.stamped-review-header-title) {
        @apply text-lg;
    }
    /* pagination */
    :deep(.stamped-pagination) {
        @apply flex max-w-fit items-center;
    }
    :deep(.stamped-pagination .previous),
    :deep(.stamped-pagination .next) {
        @apply flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-woom-white text-black shadow-[0_1px_5px_rgba(0,0,0,0.2),0_3px_1px_-2px_rgba(0,0,0,0.12),0_2px_2px_rgba(0,0,0,0.14)] transition-colors hover:bg-woom-black hover:text-white;
    }
    :deep(.stamped-pagination .previous a),
    :deep(.stamped-pagination .next a) {
        @apply text-lg;
    }
</style>
